import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { ArrowRight, Zap, Battery, Mountain, Building, ChevronDown, Eye, X, Plus, Check } from 'lucide-react';
import { brandInfo, bikes, getHighlightedBikes } from '../data/bikes';
import { useLanguage } from '../i18n';
import { useTheme } from '../context/ThemeContext';
import CatalogFilters from '../components/CatalogFilters';
import { ComparisonModal, CompareBar } from '../components/BikeComparison';
import SEO from '../components/SEO';

const MAX_COMPARE = 3;

// Category Icon Component
const CategoryIcon = ({ icon, className }) => {
  const icons = {
    mountain: Mountain,
    trees: Mountain,
    building: Building,
    fold: ChevronDown
  };
  const IconComponent = icons[icon] || Zap;
  return <IconComponent className={className} />;
};

// Quick View Modal Component
const QuickViewModal = ({ bike, onClose }) => {
  const { t } = useLanguage();
  
  if (!bike) return null;
  
  const bikeContent = {
    tagline: t(`bikes.${bike.slug}.tagline`) || bike.tagline,
    description: t(`bikes.${bike.slug}.description`) || bike.description,
    color: t(`bikes.${bike.slug}.color`) || bike.color,
    features: t(`bikes.${bike.slug}.features`) || bike.features
  };
  
  const categoryName = t(`categories.${bike.category}.name`);
  const finalPrice = bike.pricing.launch || bike.pricing.promo || bike.pricing.list;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-[#12121a] border border-[#2a2a3a] rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#1a1a25] flex items-center justify-center text-white/60 hover:text-white hover:bg-[#B5A16A] transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="grid md:grid-cols-2 gap-6 p-6">
          {/* Image */}
          <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[#1a1a25] to-[#0f0f18]">
            {(() => {
              const img = bike.images[0];
              const webpPath = img && !img.startsWith('http') && img.endsWith('.png') 
                ? img.replace(/\.png$/i, '.webp') 
                : null;
              
              return webpPath ? (
                <picture>
                  <source srcSet={webpPath} type="image/webp" />
                  <img 
                    src={img} 
                    alt={bike.name}
                    loading="lazy"
                    className="w-full h-full object-contain p-6"
                  />
                </picture>
              ) : (
                <img 
                  src={img || '/bikes/navy.png'} 
                  alt={bike.name}
                  loading="lazy"
                  className="w-full h-full object-contain p-6"
                />
              );
            })()}
          </div>
          
          {/* Info */}
          <div className="flex flex-col">
            <span className="text-[#B5A16A] text-xs font-medium uppercase tracking-wider mb-2">
              {categoryName}
            </span>
            
            <h2 className="text-3xl font-bold text-white mb-2">{bike.name}</h2>
            <p className="text-[#B5A16A] mb-4">{bikeContent?.tagline || bike.tagline}</p>
            <p className="text-white/60 text-sm mb-6">{bikeContent?.description || bike.description}</p>
            
            {/* Quick Specs */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-[#1a1a25] rounded-xl p-3">
                <Zap className="w-4 h-4 text-[#B5A16A] mb-1" />
                <p className="text-white/50 text-xs">{t('specs.motor')}</p>
                <p className="text-white text-sm font-medium">{bike.specs.motor}</p>
              </div>
              <div className="bg-[#1a1a25] rounded-xl p-3">
                <Battery className="w-4 h-4 text-[#B5A16A] mb-1" />
                <p className="text-white/50 text-xs">{t('specs.battery')}</p>
                <p className="text-white text-sm font-medium">{bike.specs.battery}</p>
              </div>
            </div>
            
            {/* Features */}
            <div className="mb-6">
              <p className="text-white/50 text-xs mb-2 uppercase">{t('product.mainFeatures')}</p>
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(bikeContent.features) ? bikeContent.features : bike.features).map((feature, i) => (
                  <span key={i} className="px-3 py-1 bg-[#B5A16A]/10 text-[#B5A16A] text-xs rounded-full">
                    {feature}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Price & CTA */}
            <div className="mt-auto">
              <div className="flex items-end gap-3 mb-4">
                {bike.pricing.launch && (
                  <span className="text-white/50 line-through">€{bike.pricing.list.toLocaleString()}</span>
                )}
                <span className="text-3xl font-bold text-[#B5A16A]">€{finalPrice.toLocaleString()}</span>
                {bike.pricing.launch && (
                  <span className="px-2 py-1 bg-[#ef4444] text-white text-xs font-bold rounded">
                    -{Math.round((1 - finalPrice / bike.pricing.list) * 100)}%
                  </span>
                )}
              </div>
              
              <Link
                to={`/bikes/${bike.slug}`}
                className="btn-primary w-full text-center block"
                onClick={onClose}
              >
                {t('product.back') === 'Indietro' ? 'Vedi Dettagli' : t('product.back') === 'Atrás' ? 'Ver Detalles' : 'View Details'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hero Section for Homepage
const HomeHero = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  
  const scrollToCatalog = () => {
    const element = document.querySelector('#catalogo');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  return (
    <section className={`relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-24 pb-16 ${
      isDark ? 'bg-[#0a0a0f]' : 'bg-gradient-to-t from-white via-amber-50 to-yellow-50'
    }`}>
      {/* Background Effects */}
      {isDark ? (
        <>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0f0f18] to-[#0a0a0f]" />
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B5A16A] rounded-full filter blur-[150px] opacity-40" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#B5A16A] rounded-full filter blur-[150px] opacity-30" />
          </div>
        </>
      ) : (
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-white via-amber-50 to-yellow-50" />
        </div>
      )}
      
      <div className="container mx-auto px-4 relative z-10 text-center">
        <div data-aos="fade-up" className="mb-6">
          <img 
            src={brandInfo.logo} 
            alt={brandInfo.name}
            loading="eager"
            className={`h-32 md:h-56 lg:h-72 mx-auto mb-10 ${isDark ? 'drop-shadow-[0_0_40px_rgba(181,161,106,0.6)]' : 'drop-shadow-lg'}`}
          />
        </div>
        
        {/* SEO Optimized H1 */}
        <h1 
          data-aos="fade-up" 
          data-aos-delay="50"
          className={`heading-1 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
        >
          <span className={isDark ? 'gradient-text-gold' : 'gradient-text-gold-light'}>Freedom is Victory</span>
        </h1>
        
        <p 
          data-aos="fade-up" 
          data-aos-delay="100"
          className={`body-large max-w-3xl mx-auto mb-8 ${isDark ? 'text-white/80' : 'text-gray-600'}`}
        >
          {t('brand.description')}
        </p>
        
        <div 
          data-aos="fade-up" 
          data-aos-delay="200"
          className="flex flex-wrap gap-4 justify-center"
        >
          <button 
            onClick={scrollToCatalog}
            className="btn-primary mx-auto"
          >
            {t('hero.exploreCatalog')}
          </button>
        </div>
      </div>
    </section>
  );
};

// Highlighted Bikes Section
const HighlightedBikes = ({ onQuickView, compareBikes, onCompareToggle }) => {
  const highlighted = getHighlightedBikes();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  
  return (
    <section className={`py-16 ${isDark ? 'bg-[#0f0f18]' : 'bg-white'}`}>
      <div className="container mx-auto px-4">
        <h2 
          data-aos="fade-up"
          className="heading-1 text-center mb-4"
        >
          <span className={isDark ? 'gradient-text-gold' : 'gradient-text-gold-light'}>{t('highlighted.title')}</span>
        </h2>
        <p 
          data-aos="fade-up"
          data-aos-delay="100"
          className="body-medium text-center text-white/70 mb-12 max-w-2xl mx-auto"
        >
          {t('highlighted.subtitle')}
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {highlighted.map((bike, index) => (
            <BikeCard 
              key={bike.id} 
              bike={bike} 
              index={index} 
              featured 
              onQuickView={onQuickView}
              isCompareSelected={compareBikes.some(b => b.id === bike.id)}
              onCompareToggle={onCompareToggle}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Get motor power from specs
const getMotorPower = (specs) => {
  const match = specs.motor.match(/(\d+)\s*Nm/);
  return match ? match[1] : null;
};

// Bike Card Component with Quick View, Compare toggle, and informative badges
const BikeCard = ({ bike, index, featured = false, onQuickView, isCompareSelected, onCompareToggle }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const categoryName = t(`categories.${bike.category}.name`);
  const bikeTagline = t(`bikes.${bike.slug}.tagline`) || bike.tagline;
  const motorPower = getMotorPower(bike.specs);
  
  // Determine badge type
  const getBadgeInfo = () => {
    if (bike.specs.motor.includes('130')) return { text: '130 Nm', color: 'from-[#B5A16A] to-[#8a7a52]' };
    if (bike.specs.motor.includes('80')) return { text: '80 Nm', color: 'from-[#B5A16A] to-[#c4b17a]' };
    if (bike.category === 'folding') return { text: t('nav.folding'), color: 'from-[#10b981] to-[#059669]' };
    return null;
  };
  
  const badge = getBadgeInfo();
  
  return (
    <div 
      data-aos="fade-up"
      data-aos-delay={index * 100}
      data-testid={`bike-card-${bike.slug}`}
      className={`group relative rounded-3xl overflow-hidden border transition-all duration-300 ${
        isDark 
          ? `bg-[#12121a] border-[#2a2a3a] hover:border-[#B5A16A] hover:shadow-[0_0_40px_rgba(181,161,106,0.2)] ${featured ? 'ring-2 ring-[#B5A16A]/30' : ''}`
          : `bg-white border-gray-200 hover:border-[#B5A16A] hover:shadow-xl shadow-md ${featured ? 'ring-2 ring-[#B5A16A]/30' : ''}`
      } ${isCompareSelected ? 'ring-2 ring-[#B5A16A]' : ''}`}
    >
      {/* Quick View Button */}
      <button
        onClick={() => onQuickView(bike)}
        className={`absolute top-4 left-4 z-10 w-10 h-10 rounded-full border flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 ${
          isDark 
            ? 'bg-[#12121a]/80 border-[#B5A16A]/30 text-white/60 hover:text-white hover:bg-[#B5A16A]'
            : 'bg-white/90 border-gray-300 text-gray-500 hover:text-white hover:bg-[#B5A16A]'
        }`}
        title="Quick View"
        data-testid={`quick-view-${bike.slug}`}
      >
        <Eye className="w-5 h-5" />
      </button>
      
      {/* Image Container */}
      <Link to={`/bikes/${bike.slug}`} className="block">
        <div className={`relative aspect-[4/3] overflow-hidden ${
          isDark 
            ? 'bg-gradient-to-br from-[#1a1a25] to-[#0f0f18]'
            : 'bg-gradient-to-br from-gray-50 to-gray-100'
        }`}>
          {(() => {
            const img = bike.images[0];
            const webpPath = img && !img.startsWith('http') && (img.endsWith('.png') || img.endsWith('.jpg') || img.endsWith('.jpeg'))
              ? (img.endsWith('.png') ? img.replace(/\.png$/i, '.webp') : img.replace(/\.(jpg|jpeg)$/i, '.webp'))
              : null;
            
            return webpPath ? (
              <picture>
                <source srcSet={webpPath} type="image/webp" />
                <img 
                  src={img} 
                  alt={bike.name}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                />
              </picture>
            ) : (
              <img 
                src={img} 
                alt={bike.name}
                loading="lazy"
                className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
              />
            );
          })()}
          
          {/* Informative Badge */}
          {badge && (
            <div className={`absolute top-4 right-4 bg-gradient-to-r ${badge.color} text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1`}>
              <Zap className="w-3 h-3" />
              {badge.text}
            </div>
          )}
          
          {/* Promo Badge */}
          {bike.pricing.launch && (
            <div className="absolute bottom-4 right-4 bg-[#ef4444] text-white text-xs font-bold px-3 py-1 rounded-full">
              -{Math.round((1 - bike.pricing.launch / bike.pricing.list) * 100)}%
            </div>
          )}
        </div>
      </Link>
      
      {/* Content */}
      <Link to={`/bikes/${bike.slug}`} className="block p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`}>
            {categoryName}
          </span>
        </div>
        
        <h3 className={`heading-3 mb-2 transition-colors ${isDark ? 'text-white group-hover:text-[#B5A16A]' : 'text-gray-900 group-hover:text-[#8a7a52]'}`}>
          {bike.name}
        </h3>
        
        <p className={`body-small mb-4 line-clamp-2 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          {bikeTagline}
        </p>
        
        {/* Specs Preview */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <div className={`flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            <Zap className={`w-4 h-4 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
            <span>{motorPower} Nm</span>
          </div>
          <div className={`flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            <Battery className={`w-4 h-4 ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
            <span>{bike.specs.battery.split('/')[1]?.trim()}</span>
          </div>
        </div>
        
        {/* Price and Actions */}
        <div className="flex items-end justify-between">
          <div>
            {bike.pricing.launch ? (
              <>
                <span className={`line-through text-sm mr-2 ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                  €{bike.pricing.list.toLocaleString()}
                </span>
                <span className={`text-2xl font-bold ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`}>
                  €{bike.pricing.launch.toLocaleString()}
                </span>
              </>
            ) : (
              <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                €{bike.pricing.list.toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Compare Toggle Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCompareToggle(bike);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                isCompareSelected 
                  ? 'bg-[#B5A16A] text-white' 
                  : isDark 
                    ? 'bg-[#1a1a25] border border-[#2a2a3a] text-white/60 hover:text-white hover:border-[#B5A16A]'
                    : 'bg-gray-100 border border-gray-300 text-gray-500 hover:text-[#8a7a52] hover:border-[#B5A16A]'
              }`}
              data-testid={`compare-toggle-${bike.slug}`}
            >
              {isCompareSelected ? (
                <>
                  <Check className="w-3 h-3 inline mr-1" />
                  {t('compare.added')}
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3 inline mr-1" />
                  {t('compare.add')}
                </>
              )}
            </button>
            <ArrowRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${isDark ? 'text-[#B5A16A]' : 'text-[#8a7a52]'}`} />
          </div>
        </div>
      </Link>
    </div>
  );
};

// Category Section
const CategorySection = ({ category, filteredBikes, onQuickView, compareBikes, onCompareToggle }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const categoryBikes = filteredBikes.filter(b => b.category === category.id);
  
  if (categoryBikes.length === 0) return null;
  
  const categoryName = t(`categories.${category.id}.name`);
  const categoryDesc = t(`categories.${category.id}.description`);
  
  return (
    <section id={`category-${category.id}`} className="py-16">
      <div className="container mx-auto px-4">
        <div className="flex items-center gap-4 mb-8" data-aos="fade-up">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#B5A16A] via-[#c4b17a] to-[#B5A16A] flex items-center justify-center">
            <CategoryIcon icon={category.icon} className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className={`heading-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{categoryName}</h2>
            <p className={`body-small ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{categoryDesc}</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categoryBikes.map((bike, index) => (
            <BikeCard 
              key={bike.id} 
              bike={bike} 
              index={index} 
              onQuickView={onQuickView}
              isCompareSelected={compareBikes.some(b => b.id === bike.id)}
              onCompareToggle={onCompareToggle}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Full Catalog Section with Filters
const CatalogSection = ({ onQuickView, compareBikes, onCompareToggle }) => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [filters, setFilters] = useState({ power: [], price: [], features: [], motorType: [] });
  
  // Categories data (Folding Fatbike first)
  const categories = [
    { id: "folding", icon: "fold" },
    { id: "fatbike", icon: "mountain" },
    { id: "mtb", icon: "trees" },
    { id: "urban", icon: "building" }
  ];
  
  // Filter bikes
  const filteredBikes = bikes.filter(bike => {
    // Power filter
    if (filters.power.length > 0) {
      const motorPower = getMotorPower(bike.specs);
      if (!filters.power.includes(motorPower)) return false;
    }
    
    // Price filter
    if (filters.price.length > 0) {
      const price = bike.pricing.launch || bike.pricing.list;
      const inRange = filters.price.some(range => {
        if (range === '0-1500') return price < 1500;
        if (range === '1500-2000') return price >= 1500 && price < 2000;
        if (range === '2000-3000') return price >= 2000 && price < 3000;
        if (range === '3000+') return price >= 3000;
        return false;
      });
      if (!inRange) return false;
    }
    
    // Features filter
    if (filters.features.length > 0) {
      if (filters.features.includes('suspension') && !bike.features.some(f => f.toLowerCase().includes('suspension') || f.toLowerCase().includes('ammortizzatore'))) return false;
      if (filters.features.includes('folding') && bike.category !== 'folding') return false;
      if (filters.features.includes('promo') && !bike.pricing.launch) return false;
    }
    
    // Motor type filter
    if (filters.motorType && filters.motorType.length > 0) {
      if (!bike.motorType || !filters.motorType.includes(bike.motorType)) return false;
    }
    
    return true;
  });
  
  const resultsText = {
    it: `${filteredBikes.length} ${filteredBikes.length === 1 ? 'modello trovato' : 'modelli trovati'}`,
    en: `${filteredBikes.length} ${filteredBikes.length === 1 ? 'model found' : 'models found'}`,
    es: `${filteredBikes.length} ${filteredBikes.length === 1 ? 'modelo encontrado' : 'modelos encontrados'}`
  };
  
  const [hasOpenDropdown, setHasOpenDropdown] = useState(false);
  
  return (
    <section id="catalogo" className={`py-16 ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} ${hasOpenDropdown ? 'pb-80' : ''}`}>
      <div className="container mx-auto px-4">
        <h2 
          data-aos="fade-up"
          className="heading-1 text-center mb-4"
        >
          <span className={isDark ? 'gradient-text-gold' : 'gradient-text-gold-light'}>{t('catalog.title')}</span>
        </h2>
        <p 
          data-aos="fade-up"
          data-aos-delay="100"
          className={`body-medium text-center mb-8 max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}
        >
          {t('catalog.subtitle')}
        </p>
        
        {/* Results count */}
        <p className={`text-center mb-8 ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-aos="fade-up">
          {resultsText[language] || resultsText.en}
        </p>
        
        {/* Filters */}
        <div data-aos="fade-up" className="relative" style={{ zIndex: 50, overflow: 'visible' }}>
          <CatalogFilters 
            bikes={filteredBikes} 
            onFilterChange={setFilters} 
            activeFilters={filters}
            onDropdownChange={setHasOpenDropdown}
          />
        </div>
        
        {filteredBikes.length === 0 ? (
          <div className="text-center py-16">
            <p className={`text-lg ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {language === 'it' ? 'Nessun modello corrisponde ai filtri selezionati' : 
               language === 'es' ? 'Ningún modelo coincide con los filtros seleccionados' : 
               'No models match the selected filters'}
            </p>
          </div>
        ) : (
          categories.map(category => (
            <CategorySection 
              key={category.id} 
              category={category} 
              filteredBikes={filteredBikes}
              onQuickView={onQuickView}
              compareBikes={compareBikes}
              onCompareToggle={onCompareToggle}
            />
          ))
        )}
      </div>
    </section>
  );
};

// Brand Info Section
const BrandSection = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  
  return (
    <section className={`py-20 ${isDark ? 'bg-[#12121a]' : 'bg-slate-100'}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            data-aos="fade-up"
            className={`heading-1 mb-6 ${isDark ? '' : 'text-gray-900'}`}
          >
            {t('brand.title')} <span className={isDark ? 'gradient-text-gold' : 'gradient-text-gold-light'}>Viketory</span>?
          </h2>
          <p 
            data-aos="fade-up"
            data-aos-delay="100"
            className={`body-large mb-12 ${isDark ? 'text-white/70' : 'text-gray-600'}`}
          >
            {t('brand.manufacturer')}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8" data-aos="fade-up" data-aos-delay="200">
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#0f0f18] border-[#2a2a3a]' : 'bg-white border-gray-200 shadow-md'}`}>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#B5A16A] via-[#c4b17a] to-[#B5A16A] flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className={`heading-3 mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('brand.power')}</h3>
              <p className={`body-small ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('brand.powerDesc')}</p>
            </div>
            
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#0f0f18] border-[#2a2a3a]' : 'bg-white border-gray-200 shadow-md'}`}>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#B5A16A] via-[#c4b17a] to-[#B5A16A] flex items-center justify-center mx-auto mb-4">
                <Battery className="w-7 h-7 text-white" />
              </div>
              <h3 className={`heading-3 mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('brand.range')}</h3>
              <p className={`body-small ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('brand.rangeDesc')}</p>
            </div>
            
            <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#0f0f18] border-[#2a2a3a]' : 'bg-white border-gray-200 shadow-md'}`}>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#B5A16A] via-[#c4b17a] to-[#B5A16A] flex items-center justify-center mx-auto mb-4">
                <Mountain className="w-7 h-7 text-white" />
              </div>
              <h3 className={`heading-3 mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('brand.versatility')}</h3>
              <p className={`body-small ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('brand.versatilityDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main HomePage Component
const HomePage = () => {
  const [quickViewBike, setQuickViewBike] = useState(null);
  const [compareBikes, setCompareBikes] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const { theme } = useTheme();
  
  // Handle compare toggle
  const handleCompareToggle = (bike) => {
    setCompareBikes(prev => {
      const isSelected = prev.some(b => b.id === bike.id);
      if (isSelected) {
        return prev.filter(b => b.id !== bike.id);
      }
      if (prev.length >= MAX_COMPARE) {
        // Remove first, add new
        return [...prev.slice(1), bike];
      }
      return [...prev, bike];
    });
  };
  
  // Handle remove from compare
  const handleCompareRemove = (bikeId) => {
    setCompareBikes(prev => prev.filter(b => b.id !== bikeId));
  };
  
  // Handle add to compare (from modal)
  const handleCompareAdd = (bike) => {
    if (compareBikes.length < MAX_COMPARE) {
      setCompareBikes(prev => [...prev, bike]);
    }
  };
  
  useEffect(() => {
    // Disable AOS on mobile for better performance
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      AOS.init({
        duration: 600,
        easing: 'ease-out',
        once: true, // Changed to true for better performance
        offset: 50,
        disable: isMobile ? 'mobile' : false,
      });
    }
  }, []);
  
  // Refresh AOS when theme changes (only on desktop)
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      AOS.refresh();
    }
  }, [theme]);

  const { t, language } = useLanguage();
  
  // Structured Data for Organization and Website
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Viketory",
    "alternateName": "FreeWheel Store",
    "url": "https://www.viketory.it",
    "logo": "https://www.viketory.it/bikes/navy.png",
    "description": brandInfo.description,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Via Salvador Allende, 33",
      "addressLocality": "Castellammare di Stabia",
      "postalCode": "80053",
      "addressRegion": "NA",
      "addressCountry": "IT"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+39-081-314-94-11",
      "contactType": "customer service",
      "email": "info@freewheel.store",
      "availableLanguage": ["Italian", "English", "Spanish"]
    },
    "sameAs": [
      "https://www.facebook.com/FreeWheelStore",
      "https://www.instagram.com/freewheelstore",
      "https://www.youtube.com/@freewheel_store",
      "https://www.tiktok.com/@freewheelstore"
    ]
  };

  const websiteStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Viketory E-Bike",
    "url": "https://www.viketory.it",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.viketory.it/?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.viketory.it"
    }]
  };

  return (
    <>
      <SEO
        title="Viketory | E-Bike di Alta Qualità - Catalogo Completo di Biciclette Elettriche"
        description="Scopri il catalogo completo di e-bike Viketory: fatbike elettriche, mountain bike, bici pieghevoli e urbane. Motori potenti fino a 130Nm, batterie 720Wh, design italiano. Spedizione inclusa, preassemblaggio 80%. Omologate CE EPAC EN15194."
        keywords="e-bike, bici elettrica, fatbike elettrica, mountain bike elettrica, bici pieghevole elettrica, viketory, freewheel store, bici elettrica italiana, ebike 80nm, ebike 130nm, bici elettrica prezzo"
        url="/"
        type="website"
        structuredData={[organizationStructuredData, websiteStructuredData, breadcrumbStructuredData]}
        canonicalUrl="https://www.viketory.it"
      />
      <main data-testid="home-page" className={`${compareBikes.length > 0 ? 'pb-20' : ''} bg-[var(--bg-page)] min-h-screen`}>
      <HomeHero />
      <HighlightedBikes 
        onQuickView={setQuickViewBike} 
        compareBikes={compareBikes}
        onCompareToggle={handleCompareToggle}
      />
      <BrandSection />
      <CatalogSection 
        onQuickView={setQuickViewBike}
        compareBikes={compareBikes}
        onCompareToggle={handleCompareToggle}
      />
      
      {/* Quick View Modal */}
      {quickViewBike && (
        <QuickViewModal bike={quickViewBike} onClose={() => setQuickViewBike(null)} />
      )}
      
      {/* Compare Bar */}
      <CompareBar 
        selectedBikes={compareBikes}
        onRemove={handleCompareRemove}
        onCompare={() => setShowCompareModal(true)}
        onClear={() => setCompareBikes([])}
      />
      
      {/* Comparison Modal */}
      {showCompareModal && (
        <ComparisonModal 
          selectedBikes={compareBikes}
          onClose={() => setShowCompareModal(false)}
          onRemove={handleCompareRemove}
          onAdd={handleCompareAdd}
        />
      )}
    </main>
    </>
  );
};

export default HomePage;
